import React, { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { toast } from '@/hooks/use-toast';
import {
  COOKIEBOT_LOAD_EVENT,
  isCookiebotAvailable,
  openCookiePreferences,
} from '@/lib/cookiebot';
import { settingsCardInSprint, settingsCrmIsNative } from '@/lib/settingsPaths';
import { SETTINGS_PATHS } from '@/lib/settingsPaths';
import { useAccountRecord } from '@/pages/account/AccountRecordContext';
import {
  ACCOUNT_ASSIST_LEVELS,
  ACCOUNT_NOTIFICATION_REQUIRED,
  ACCOUNT_NOTIFICATION_ROWS,
  ACCOUNT_SAMPLE_KINDS,
  ACCOUNT_WRITING_STYLES,
  ACCOUNT_WRITING_TONES,
  accountCount,
} from '@/pages/account/accountModel';
import { AccountDialog } from '@/pages/account/AccountOverlays';
import {
  AccountCard,
  BandHead,
  CardColumn,
  Grid,
  Muted,
  NavLink,
  Row,
  ScopeChip,
  SelectField,
  SelectRow,
  SmallButton,
  StatusChip,
  SwitchRow,
  Table,
  TextAreaField,
} from '@/pages/account/primitives';
import { SettingsPageShell } from './SettingsPageShell';
import { SETTINGS_CARDS } from './settingsSearchIndex';

/**
 * AR-390 — AIVIA Settings (the prototype's Preferences + AIVIA, less the
 * Appearance card, which D-ACCT-27 moved to Color Theme).
 *
 * Five cards. Four of them — the notification matrix, Email subscriptions,
 * AIVIA assistance and AIVIA data + privacy — are scheduled at sprint 2
 * (D-ACCT-08, D-ACCT-09). Before that sprint each takes the NOT-YET treatment:
 * the rows are on the page, so the reader can see what is coming and the
 * search can find it, but every cell reads "Not yet" rather than offering a
 * switch nobody can set. The AI writing profile is live now.
 *
 * AR-391 — with the module's flags on, the AI writing profile reads the
 * member's own `user_profiles` row: writing tone, communication style, the
 * writing samples (the four `sample_*` columns) and the AI assistance level.
 * Voice instructions ("How AIVIA should write for you") have no column and no
 * owner yet, so on a live account the row is blank rather than the sample's
 * text. The samples are read-only here until a ticket binds the write, so the
 * Add and Remove controls step aside while the list is the member's.
 */

const CHANNELS = ['In-app', 'Email', 'SMS'];

/** The permission table: what AIVIA can read, why, and where it is managed. */
const useAiPermissions = (): React.ReactNode[][] => {
  const { record, members } = useAccountRecord();
  const crmOK = settingsCrmIsNative();
  const teamN = Math.max(members.length - 1, 0);

  const crmRow = (
    category: string,
    access: string,
    why: string,
  ): React.ReactNode[] => [
    category,
    access,
    why,
    'Required',
    <StatusChip key="s" status={crmOK ? 'Connected' : 'Not connected'} />,
    <Muted key="m">{crmOK ? 'Part of the CRM' : 'Arrives with the CRM'}</Muted>,
  ];

  return [
    crmRow(
      'Clients',
      'Names, contact details, trip history and notes',
      'Personalizes answers and finds the client a question is about',
    ),
    crmRow(
      'Trips',
      'Itineraries, bookings and payment schedules',
      'Answers questions and helps with trip work',
    ),
    crmRow(
      'Proposals',
      'Proposal text, options and status',
      'Drafts and revises proposal content',
    ),
    crmRow('Tasks', 'Open tasks and due dates', 'Suggests priorities and follow-ups'),
    [
      'Email',
      'Messages in your connected mailbox',
      'Summarizes messages and drafts replies',
      'Optional',
      <StatusChip key="s" status={record.crm.gmail ? 'Connected' : 'Not connected'} />,
      crmOK ? (
        <NavLink key="m" to={SETTINGS_PATHS.crm}>
          CRM settings →
        </NavLink>
      ) : (
        <Muted key="m">Arrives with the CRM</Muted>
      ),
    ],
    [
      'Business profile',
      'Your brand, description, specialties and public profile',
      'Applies your business identity and positioning',
      'Required',
      <StatusChip key="s" status="Connected" />,
      <Muted key="m">Part of your account</Muted>,
    ],
    [
      'Writing samples',
      'The samples on your AI writing profile',
      'Matches your voice',
      'Optional',
      <StatusChip key="s" status={record.ai.voice ? 'Connected' : 'Off'} />,
      <Muted key="m">The switch below</Muted>,
    ],
    [
      'Team data',
      'Records your team shares with you',
      'Works within the access your team allows',
      'Optional',
      <StatusChip key="s" status={teamN ? 'Connected' : 'Not connected'} />,
      <Muted key="m">
        {teamN
          ? accountCount(
              teamN,
              'team member shares records with you',
              'team members share records with you',
            )
          : 'No team members yet'}
      </Muted>,
    ],
  ];
};

/** One switch in the notification matrix, or the not-yet chip in its place. */
const MatrixCell: React.FC<{
  value: number | null;
  label: string;
  live: boolean;
  onToggle: () => void;
}> = ({ value, label, live, onToggle }) => {
  if (value === null) return <Muted>—</Muted>;
  if (!live) return <StatusChip status="Not yet" />;
  const on = Boolean(value);
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={onToggle}
      className={cn(
        'relative inline-flex h-5 w-9 items-center rounded-full transition-colors',
        on ? 'bg-orange-500' : 'bg-gray-300 dark:bg-gray-600',
      )}
    >
      <span
        className={cn(
          'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
          on ? 'translate-x-[18px]' : 'translate-x-0.5',
        )}
      />
    </button>
  );
};

/** A switch that cannot be turned off — security, billing and compliance. */
const RequiredCell: React.FC = () => (
  <button
    type="button"
    role="switch"
    aria-checked
    disabled
    title="You cannot turn these off"
    className="relative inline-flex h-5 w-9 cursor-not-allowed items-center rounded-full bg-orange-500 opacity-60"
  >
    <span className="inline-block h-4 w-4 translate-x-[18px] transform rounded-full bg-white" />
  </button>
);

export const SettingsAivia: React.FC = () => {
  const {
    record,
    setLive,
    addWritingSample,
    removeWritingSample,
    setConfirm,
    isSettingsLive,
  } = useAccountRecord();
  const [sampleOpen, setSampleOpen] = useState(false);
  // Cookiebot loads asynchronously. Subscribe BEFORE re-checking: the load
  // event can fire between the first render and this effect, and a listener
  // installed after that point would never see it.
  const [cookiebotReady, setCookiebotReady] = useState(isCookiebotAvailable);
  useEffect(() => {
    const onLoad = () => setCookiebotReady(isCookiebotAvailable());
    window.addEventListener(COOKIEBOT_LOAD_EVENT, onLoad);
    onLoad();
    return () => window.removeEventListener(COOKIEBOT_LOAD_EVENT, onLoad);
  }, []);
  const [styleOpen, setStyleOpen] = useState(false);
  const [draft, setDraft] = useState({ kind: ACCOUNT_SAMPLE_KINDS[0][0], title: '', text: '' });

  // AR-391 — the model can answer while the Add dialog is open. Once the list
  // is the member's own, a Save here would write nothing, so the dialog closes
  // and says where the sample goes instead of quietly dropping it.
  useEffect(() => {
    if (!isSettingsLive || !sampleOpen) return;
    setSampleOpen(false);
    toast({
      title: 'Writing samples are read from your profile now',
      description: 'Add this sample on your profile page for now — nothing was saved here.',
    });
  }, [isSettingsLive, sampleOpen]);

  const perms = useAiPermissions();
  const notifLive = settingsCardInSprint('notifications');
  const emailLive = settingsCardInSprint('email');
  const assistLive = settingsCardInSprint('assist');
  const dataLive = settingsCardInSprint('aidata');

  const v = record.voice;
  const ai = record.ai;
  const e = record.email;

  const notifRows: React.ReactNode[][] = [
    ...ACCOUNT_NOTIFICATION_REQUIRED.map((name) => [
      <span key="l" className="flex flex-wrap items-center gap-2">
        <b className="text-gray-900 dark:text-gray-100">{name}</b>
        <StatusChip status="Required" />
      </span>,
      <RequiredCell key="a" />,
      <RequiredCell key="b" />,
      <Muted key="c">—</Muted>,
    ]),
    ...ACCOUNT_NOTIFICATION_ROWS.map(([key, label]) => [
      <b key="l" className="text-gray-900 dark:text-gray-100">
        {label}
      </b>,
      ...CHANNELS.map((channel, i) => (
        <MatrixCell
          key={channel}
          value={record.notif.rows[key]?.[i] ?? null}
          label={`${label}, ${channel}`}
          live={notifLive}
          onToggle={() =>
            setLive(`notif.rows.${key}.${i}`, record.notif.rows[key][i] ? 0 : 1)
          }
        />
      )),
    ]),
  ];

  // The counts line is derived from the list, so a sample added or removed
  // moves it — the row and the value it describes cannot drift apart.
  const sampleCounts = ACCOUNT_SAMPLE_KINDS.map(([kind, one, many]) => {
    const n = v.samples.filter((s) => s.kind === kind).length;
    return n ? accountCount(n, one, many) : '';
  })
    .filter(Boolean)
    .join(', ');

  const saveSample = () => {
    if (isSettingsLive) return;
    const title = draft.title.trim();
    if (!title) return;
    const words = draft.text.trim() ? draft.text.trim().split(/\s+/).length : 0;
    addWritingSample(draft.kind, title, words);
    setDraft({ kind: ACCOUNT_SAMPLE_KINDS[0][0], title: '', text: '' });
    setSampleOpen(false);
  };

  return (
    <SettingsPageShell page="aivia">
      <CardColumn wide>
        {/* Notifications — D-ACCT-09, scheduled at sprint 2. */}
        <AccountCard name={SETTINGS_CARDS.notifications.name} chip={<ScopeChip scope="personal" />}>
          <Table
            columns={['Notification type', ...CHANNELS]}
            rows={notifRows}
            matrix
          />
          <p className="mt-2.5">
            <Muted>
              <NavLink to="/notifications">Delivery rules →</NavLink>
            </Muted>
          </p>
        </AccountCard>

        {/* Email subscriptions — D-ACCT-08, scheduled at sprint 2. */}
        <AccountCard
          name={SETTINGS_CARDS.emailSubs.name}
          chip={<ScopeChip scope="personal" />}
          lead="Marketing email. Account, billing and security email is always sent."
        >
          {emailLive ? (
            <>
              <SwitchRow
                label="Weekly digest"
                checked={e.digest}
                onChange={(next) => setLive('email.digest', next)}
              />
              <SelectRow
                label="Promotions digest"
                value={e.promos}
                options={['Off', 'Weekly', 'Monthly']}
                onChange={(next) => setLive('email.promos', next)}
              />
              <SwitchRow
                label="Event reminders"
                checked={e.events}
                onChange={(next) => setLive('email.events', next)}
                last
              />
            </>
          ) : (
            <>
              <Row label="Weekly digest" chip={<StatusChip status="Not yet" />} />
              <Row label="Promotions digest" chip={<StatusChip status="Not yet" />} />
              <Row label="Event reminders" chip={<StatusChip status="Not yet" />} last />
            </>
          )}
        </AccountCard>

        {/* The AI writing profile is live now.
            AR-391 — once the card shows the member's own profile it has no
            Edit: the bound fields are read-only in this ticket, and a Save
            that wrote only the scratch copy would look successful and then
            vanish under the overlay. The card gets its form back with the
            ticket that binds the write. */}
        <AccountCard
          cardKey={isSettingsLive ? undefined : 'voice'}
          name={SETTINGS_CARDS.voice.name}
          chip={<ScopeChip scope="personal" />}
          lead={
            isSettingsLive
              ? 'AIVIA uses these preferences and samples to write in your voice. Your samples are never shown publicly. Editing them here lands with the write — your profile page changes them today.'
              : 'AIVIA uses these preferences and samples to write in your voice. Your samples are never shown publicly.'
          }
          form={
            <>
              <SelectField path="voice.tone" label="Writing tone" options={ACCOUNT_WRITING_TONES} />
              <SelectField
                path="voice.style"
                label="Communication style"
                options={ACCOUNT_WRITING_STYLES}
              />
              {/* D-ACCT-27 (Britta) — "Additional voice instructions" read as
                  spoken input, so the label says what it is for. */}
              <TextAreaField
                path="voice.notes"
                label="How AIVIA should write for you"
                hint="Free text. Anything about tone or wording the choices above do not cover."
              />
            </>
          }
        >
          <Row label="Writing tone" value={v.tone} />
          <Row label="Communication style" value={v.style} />
          <Row label="How AIVIA should write for you" value={v.notes || 'None'} />
          <Row label="Writing samples" value={sampleCounts || 'None yet'} last>
            <div className="mt-2 space-y-1.5">
              {v.samples.length ? (
                v.samples.map((s, i) => (
                  <div
                    key={`${s.title}-${i}`}
                    className="flex flex-wrap items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5 dark:border-gray-700"
                  >
                    <span className="inline-flex flex-none items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                      {s.kind}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[14px] text-gray-900 dark:text-gray-100">
                      {s.title}
                    </span>
                    <Muted>{accountCount(s.words, 'word', 'words')}</Muted>
                    {s.added ? <Muted>Added {s.added}</Muted> : null}
                    {/* AR-391 — the list is the member's own once the model
                        answers, and nothing here writes it back yet. */}
                    {isSettingsLive ? null : (
                      <>
                        <button
                          type="button"
                          disabled
                          title="Replacing a sample lands with the file store"
                          className="cursor-not-allowed text-[13px] text-gray-400 dark:text-gray-500"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={() => removeWritingSample(i)}
                          aria-label={`Remove ${s.title}`}
                          className="text-[13px] text-orange-700 hover:underline dark:text-orange-400"
                        >
                          Remove
                        </button>
                      </>
                    )}
                  </div>
                ))
              ) : (
                <Muted>Nothing yet.</Muted>
              )}
            </div>
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {isSettingsLive ? (
                <SmallButton
                  disabled
                  title="Adding a sample from here lands with the write — your profile page adds them today"
                >
                  Add writing sample
                </SmallButton>
              ) : (
                <SmallButton onClick={() => setSampleOpen(true)}>
                  Add writing sample
                </SmallButton>
              )}
              <SmallButton onClick={() => setStyleOpen(true)}>
                Preview how AIVIA reads your style
              </SmallButton>
            </div>
          </Row>
        </AccountCard>

        {/* AIVIA assistance — D-ACCT-09, scheduled at sprint 2. */}
        <AccountCard name={SETTINGS_CARDS.assist.name} chip={<ScopeChip scope="personal" />}>
          <div role={assistLive ? 'radiogroup' : undefined} aria-label="AIVIA assistance">
            <Grid cols={3}>
              {ACCOUNT_ASSIST_LEVELS.map(([level, description]) =>
                assistLive ? (
                  <button
                    key={level}
                    type="button"
                    role="radio"
                    aria-checked={v.assist === level}
                    onClick={() => setLive('voice.assist', level)}
                    className={cn(
                      'rounded-lg border px-3 py-2.5 text-left',
                      v.assist === level
                        ? 'border-orange-400 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/20'
                        : 'border-gray-200 bg-white hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800',
                    )}
                  >
                    <b className="block text-[15px] text-gray-900 dark:text-gray-100">
                      {level}
                    </b>
                    <small className="mt-0.5 block text-[13.5px] text-gray-500 dark:text-gray-400">
                      {description}
                    </small>
                  </button>
                ) : (
                  /* AR-391 — the level is bound (`ai_assistance_level`), so the
                     member's current one is marked even while the control
                     itself waits for sprint 2. */
                  <div
                    key={level}
                    aria-current={v.assist === level ? 'true' : undefined}
                    className={cn(
                      'rounded-lg border px-3 py-2.5',
                      v.assist === level
                        ? 'border-orange-400 bg-orange-50 dark:border-orange-600 dark:bg-orange-900/20'
                        : 'border-gray-200 dark:border-gray-700',
                    )}
                  >
                    <b className="mr-2 text-[15px] text-gray-900 dark:text-gray-100">
                      {level}
                    </b>
                    <StatusChip status={v.assist === level ? 'Current' : 'Not yet'} />
                    <small className="mt-0.5 block text-[13.5px] text-gray-500 dark:text-gray-400">
                      {description}
                    </small>
                  </div>
                ),
              )}
            </Grid>
          </div>
        </AccountCard>

        {/* AIVIA data + privacy — D-ACCT-09, scheduled at sprint 2. */}
        <AccountCard name={SETTINGS_CARDS.aidata.name} chip={<ScopeChip scope="personal" />}>
          <BandHead className="mt-1">Connected data + permissions</BandHead>
          <Table
            columns={[
              'Data category',
              'What AIVIA can access',
              'Why it uses it',
              'Access',
              'Status',
              'Manage',
            ]}
            rows={perms}
          />

          <Grid cols={2} className="mt-3.5">
            <div>
              <BandHead>Memory</BandHead>
              {dataLive ? (
                <SwitchRow
                  label="Memory"
                  checked={ai.memory}
                  onChange={(next) => setLive('ai.memory', next)}
                />
              ) : (
                <Row label="Memory" chip={<StatusChip status="Not yet" />} />
              )}
              <Row label="Last cleared" value={ai.cleared || 'Never'} last />
              <div className="flex flex-wrap gap-1.5 pt-2">
                <SmallButton disabled title="Memory controls land with the AIVIA build">
                  View memories
                </SmallButton>
                {/* AR-278 — live ahead of the card's sprint: the reset is a real
                    server call now, and members need it before sprint 2. */}
                <SmallButton onClick={() => setConfirm({ kind: 'memory' })}>
                  Clear all memory
                </SmallButton>
              </div>

              <BandHead className="mt-3.5">Use my writing samples</BandHead>
              {dataLive ? (
                <SwitchRow
                  label="Use my writing samples"
                  checked={ai.voice}
                  onChange={(next) => setLive('ai.voice', next)}
                />
              ) : (
                <Row label="Use my writing samples" chip={<StatusChip status="Not yet" />} />
              )}
              <p className="py-2.5">
                <Muted>
                  {accountCount(v.samples.length, 'sample', 'samples')} on your AI
                  writing profile.
                </Muted>
              </p>
            </div>

            <div>
              <BandHead>Export your data</BandHead>
              <Row
                label="What is included"
                value="Your profile, preferences, writing samples and AIVIA threads. CRM records export from the CRM."
              />
              <Row label="Format" value="A ZIP of CSV and JSON files" />
              {ai.export ? (
                <Row
                  label="Status"
                  value={`Requested ${ai.export.on}. You will get an email when it is ready.`}
                  chip={<StatusChip status={ai.export.status} />}
                  last
                />
              ) : (
                <Row label="Status" chip={<StatusChip status="Not requested" />} last />
              )}
              {dataLive ? (
                <div className="pt-2">
                  <SmallButton
                    disabled={Boolean(ai.export)}
                    title={ai.export ? 'An export is already being prepared' : undefined}
                    onClick={() =>
                      setLive('ai.export', {
                        on: new Date().toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        }),
                        status: 'Preparing',
                      })
                    }
                  >
                    Request data export
                  </SmallButton>
                </div>
              ) : null}
            </div>
          </Grid>
        </AccountCard>
        {/* NOT in the prototype, and deliberately so.
            Cookie consent (AR-269) was reachable only from the legacy Settings
            page, and this module's `/settings` redirect takes that page away —
            which would leave a member with no in-app way to change what they
            consented to. It is data-and-privacy, so it sits with the rest of
            it. The card only appears once Cookiebot's API is ready, the same
            condition the legacy page used: a button that cannot open the
            dialog is worse than no button. */}
        {cookiebotReady ? (
          <AccountCard
            name={SETTINGS_CARDS.cookies.name}
            chip={<ScopeChip scope="personal" />}
          >
            <Row
              label="Cookie preferences"
              value="Review or change which cookies AIVIA may use on this device."
              last
            />
            <div className="pt-2">
              <SmallButton onClick={openCookiePreferences}>
                Review cookie preferences
              </SmallButton>
            </div>
          </AccountCard>
        ) : null}
      </CardColumn>

      {sampleOpen ? (
        <AccountDialog
          title="Add writing sample"
          sub="Paste something you wrote. AIVIA reads it for voice, never shows it publicly."
          onClose={() => setSampleOpen(false)}
          actions={
            <>
              <SmallButton primary onClick={saveSample}>
                Save
              </SmallButton>
              <SmallButton onClick={() => setSampleOpen(false)}>Cancel</SmallButton>
            </>
          }
        >
          <div className="space-y-3">
            <label className="block">
              <span className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                Sample type
              </span>
              <select
                value={draft.kind}
                onChange={(ev) => setDraft({ ...draft, kind: ev.target.value })}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              >
                {ACCOUNT_SAMPLE_KINDS.map(([kind]) => (
                  <option key={kind} value={kind}>
                    {kind}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                Title
              </span>
              <input
                type="text"
                value={draft.title}
                placeholder="What the piece is called"
                onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[13px] font-semibold uppercase tracking-[0.1em] text-gray-500 dark:text-gray-400">
                Paste the writing
              </span>
              <textarea
                rows={5}
                value={draft.text}
                placeholder="Paste an email, a post or an itinerary you wrote"
                onChange={(ev) => setDraft({ ...draft, text: ev.target.value })}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-[15px] text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
              />
            </label>
          </div>
        </AccountDialog>
      ) : null}

      {styleOpen ? (
        /* A derived summary of what AIVIA has to work with — not generated
           text. It can only ever name what is on the card above it. */
        <AccountDialog
          title="How AIVIA reads your style"
          sub="Everything AIVIA has to go on, as it stands today."
          onClose={() => setStyleOpen(false)}
          actions={<SmallButton onClick={() => setStyleOpen(false)}>Close</SmallButton>}
        >
          <div>
            <Row label="Writing tone" value={v.tone} />
            <Row label="Communication style" value={v.style} />
            <Row label="Your instructions" value={v.notes || 'None'} />
            <Row
              label="Writing samples"
              value={sampleCounts || 'None yet'}
              last
            />
          </div>
        </AccountDialog>
      ) : null}
    </SettingsPageShell>
  );
};

export default SettingsAivia;
